import type { Nuxter, NuxterLinks, NuxterMergedPullRequests, NuxterProfile, NuxterSocialAccount, NuxterTranslations, NuxtersHackathon, NuxtersPeriod } from '../types/nuxters'
import { COUNTRIES } from './nuxters-countries'

/**
 * Where the contributor stats come from: committed nightly by the nuxt/nuxters CI,
 * one file per period in `public/`: `contributors.json` (all time), `contributors-30d.json`,
 * `contributors-12m.json` and `contributors-<year>.json`, plus `profiles.json` for the links of every contributor.
 */
export const NUXTERS_SOURCE = {
  repo: 'nuxt/nuxters',
  branch: 'main',
  dir: 'public'
} as const

export const NUXTERS_FIRST_YEAR = 2016

/**
 * The organizations whose public repositories are collected (forks excluded, archived ones included).
 * Keep in sync with `ORGS` in the nuxt/nuxters collector.
 */
export const NUXTERS_ORGS = ['nuxt', 'nuxt-community', 'nuxt-content', 'nuxt-hub', 'nuxt-modules', 'nuxt-ui-templates'] as const

/**
 * The framework repository and its former homes (Nuxt 3 was built in nuxt/framework, the docs lived
 * in nuxt/docs). Merged PRs, helpful issues and helpful comments there count `NUXTERS_CORE_MULTIPLIER` times.
 * Keep in sync with `CORE_REPOS` in the nuxt/nuxters collector.
 */
export const NUXTERS_CORE_REPOS = ['nuxt/nuxt', 'nuxt/framework', 'nuxt/docs'] as const
export const NUXTERS_CORE_MULTIPLIER = 2
export const NUXTERS_DEFAULT_PERIOD: NuxtersPeriod = '12m'
const ROLLING_PERIODS = ['30d', '12m'] as const

/** Below this, the all-time file is most likely a broken collection run: refuse to import it. */
export const NUXTERS_MIN_ROWS = 1000

const PERIOD_RE = /^(?:all|30d|12m|\d{4})$/
const FILE_RE = /^contributors(?:-(30d|12m|\d{4}))?\.json$/

/** `all`, `30d`, `12m`, or a year from 2016 to the current one: 14 values at most in 2026. */
export function isNuxtersPeriod(value: unknown): value is NuxtersPeriod {
  if (typeof value !== 'string' || !PERIOD_RE.test(value)) return false
  if (!/^\d{4}$/.test(value)) return true
  const year = Number(value)
  return year >= NUXTERS_FIRST_YEAR && year <= new Date().getUTCFullYear()
}

/** `contributors-30d.json` → `30d`, `contributors.json` → `all`, anything else → `null`. */
export function nuxtersPeriodFromFile(name: string): NuxtersPeriod | null {
  const match = FILE_RE.exec(name)
  if (!match) return null
  const period = match[1] ?? 'all'
  return isNuxtersPeriod(period) ? period : null
}

/** Name, website and social accounts of the contributors: not a period, imported on its own. */
export const NUXTERS_PROFILES_FILE = 'profiles.json'

export function nuxtersPeriodFile(period: NuxtersPeriod): string {
  return period === 'all' ? 'contributors.json' : `contributors-${period}.json`
}

export function nuxtersPeriodLabel(period: NuxtersPeriod): string {
  if (period === '30d') return 'Last 30 days'
  if (period === '12m') return 'Last 12 months'
  if (period === 'all') return 'All time'
  return period
}

/** The period in a sentence: "in the last 30 days", "of all time", "in 2024". */
export function nuxtersPeriodPhrase(period: NuxtersPeriod): string {
  if (period === '30d') return 'in the last 30 days'
  if (period === '12m') return 'in the last 12 months'
  if (period === 'all') return 'of all time'
  return `in ${period}`
}

/** Rolling windows, all time, then years from the newest. */
export function sortNuxtersPeriods(periods: NuxtersPeriod[]): NuxtersPeriod[] {
  const order = (period: NuxtersPeriod) => {
    const rolling = ROLLING_PERIODS.indexOf(period as typeof ROLLING_PERIODS[number])
    if (rolling !== -1) return rolling
    if (period === 'all') return ROLLING_PERIODS.length
    return ROLLING_PERIODS.length + 1 + (9999 - Number(period))
  }
  return [...new Set(periods)].sort((a, b) => order(a) - order(b))
}

/** A profile's stats over `period` (all time for `all`), or `null` when the user did not contribute in it. */
export function nuxterForPeriod(profile: NuxterProfile, period: NuxtersPeriod): Nuxter | null {
  if (period === 'all') return profile
  const stats = profile.periods.find(row => row.period === period)
  if (!stats) return null
  const { period: _period, ...rest } = stats
  return { githubId: profile.githubId, username: profile.username, firstContributionAt: profile.firstContributionAt, ...rest }
}

const DAY = 24 * 60 * 60 * 1000
const ROLLING_DAYS: Record<string, number> = { '30d': 30, '12m': 365 }

/**
 * A GitHub search of what a stat card counts over `period`, in the collected organizations.
 * Merged PRs and issues match the collector, which dates them by merge and creation day.
 * GitHub cannot search comments, so the comments link lists the issues and pull requests the user
 * commented on that were active in the period: a superset of the threads behind the count.
 * Rolling windows end on `today`: the collector runs nightly, so they can be a day off.
 */
export function nuxterGithubSearchUrl(username: string, metric: 'mergedPullRequests' | 'issues' | 'comments', period: NuxtersPeriod, today: Date): string {
  const year = /^\d{4}$/.test(period) ? period : null
  const days = ROLLING_DAYS[period]
  const start = year ? `${year}-01-01` : days ? new Date(today.getTime() - days * DAY).toISOString().slice(0, 10) : null
  const end = year ? `${year}-12-31` : null
  const range = start && (end ? `${start}..${end}` : `>=${start}`)

  const filters = {
    mergedPullRequests: [`is:pr is:merged author:${username}`, range && `merged:${range}`],
    issues: [`is:issue author:${username}`, range && `created:${range}`],
    // `updated` is the last activity: a thread commented on in a year may have been updated since.
    comments: [`commenter:${username}`, start && `updated:>=${start}`, end && `created:<=${end}`]
  }[metric]
  // Repeated `org:` qualifiers are combined with OR.
  const q = [...filters.filter(Boolean), ...NUXTERS_ORGS.map(org => `org:${org}`)].join(' ')
  // The issues tab lists pull requests too, which comments are also made on.
  return `https://github.com/search?${new URLSearchParams({ q, type: metric === 'mergedPullRequests' ? 'pullrequests' : 'issues' })}`
}

/** Pinned to a commit: the branch URL is CDN-cached for 5 minutes and can serve the previous file. */
export function nuxtersSourceUrl(sha: string, file: string): string {
  return `https://raw.githubusercontent.com/${NUXTERS_SOURCE.repo}/${sha}/${NUXTERS_SOURCE.dir}/${file}`
}

/** A file of the source that is imported: a period file or `profiles.json`. */
export function isNuxtersSourceFile(name: string): boolean {
  return name === NUXTERS_PROFILES_FILE || nuxtersPeriodFromFile(name) !== null
}

interface PushPayload {
  ref?: string
  after?: string
  deleted?: boolean
  repository?: { full_name?: string }
  commits?: Array<{ added?: string[], modified?: string[] }>
}

/**
 * The commit to import for a GitHub push, or why the push is ignored.
 * The nuxt/nuxters CI commits `contributors.json` nightly; any other push is noise.
 */
export function nuxtersPushSha(payload: PushPayload): { sha: string } | { skipped: string } {
  if (payload.repository?.full_name !== NUXTERS_SOURCE.repo) return { skipped: 'other-repository' }
  if (payload.ref !== `refs/heads/${NUXTERS_SOURCE.branch}`) return { skipped: 'other-ref' }
  if (payload.deleted || !payload.after || /^0+$/.test(payload.after)) return { skipped: 'no-head-commit' }

  const prefix = `${NUXTERS_SOURCE.dir}/`
  const touched = (payload.commits ?? []).some(commit =>
    [...(commit.added ?? []), ...(commit.modified ?? [])].some(path =>
      path.startsWith(prefix) && isNuxtersSourceFile(path.slice(prefix.length))
    )
  )
  return touched ? { sha: payload.after } : { skipped: 'contributors-unchanged' }
}

/** A row of the `nuxters` table, without the period and the sync timestamp. */
export type NuxterRow = Omit<Nuxter, 'mergedPullRequests' | 'core' | 'translations'> & {
  usernameLower: string
  prFeat: number
  prFix: number
  prDocs: number
  prChore: number
  prAll: number
  corePrFeat: number
  corePrFix: number
  corePrDocs: number
  corePrChore: number
  corePrAll: number
  coreHelpfulIssues: number
  coreHelpfulComments: number
  translationsEdited: number
  translationsVerified: number
  translationsRejected: number
}

function count(value: unknown): number {
  const number = Number(value)
  return Number.isFinite(number) && number > 0 ? Math.round(number) : 0
}

/**
 * Validate `contributors.json` and turn it into table rows, ranked by score.
 * Invalid entries are skipped, not fatal: one bad record must not block the import.
 */
export function parseNuxtersFile(data: unknown): NuxterRow[] {
  if (!Array.isArray(data)) {
    throw new TypeError('contributors.json must be an array')
  }

  const rows: NuxterRow[] = []
  const seen = new Set<number>()

  for (const entry of data) {
    if (!entry || typeof entry !== 'object') continue
    const record = entry as Record<string, unknown>

    const githubId = Number(record.githubId)
    const username = typeof record.username === 'string' ? record.username.trim() : ''
    if (!Number.isSafeInteger(githubId) || githubId <= 0 || !username || seen.has(githubId)) continue
    seen.add(githubId)

    // Older files stored the merged PR total as a plain number.
    const prs = record.merged_pull_requests
    const merged = typeof prs === 'object' && prs
      ? prs as Partial<NuxterMergedPullRequests>
      : { all: count(prs) }

    const first = typeof record.first_contribution_at === 'string' ? new Date(record.first_contribution_at) : null
    // Files from before the core bonus have no `core` object: no bonus.
    const core = (typeof record.core === 'object' && record.core ? record.core : {}) as {
      merged_pull_requests?: Partial<NuxterMergedPullRequests>
      helpful_issues?: unknown
      helpful_comments?: unknown
    }
    const coreMerged = core.merged_pull_requests ?? {}
    const translations = (typeof record.translations === 'object' && record.translations ? record.translations : {}) as Partial<Record<keyof NuxterTranslations, unknown>>

    rows.push({
      githubId,
      username,
      usernameLower: username.toLowerCase(),
      firstContributionAt: first && !Number.isNaN(first.getTime()) ? first.toISOString() : null,
      rank: 0,
      score: count(record.score),
      issues: count(record.issues),
      helpfulIssues: count(record.helpful_issues),
      comments: count(record.comments),
      helpfulComments: count(record.helpful_comments),
      reactions: count(record.reactions),
      prFeat: count(merged.feat),
      prFix: count(merged.fix),
      prDocs: count(merged.docs),
      prChore: count(merged.chore),
      prAll: count(merged.all),
      corePrFeat: count(coreMerged.feat),
      corePrFix: count(coreMerged.fix),
      corePrDocs: count(coreMerged.docs),
      corePrChore: count(coreMerged.chore),
      corePrAll: count(coreMerged.all),
      coreHelpfulIssues: count(core.helpful_issues),
      coreHelpfulComments: count(core.helpful_comments),
      translationsEdited: count(translations.edited),
      translationsVerified: count(translations.verified),
      translationsRejected: count(translations.rejected)
    })
  }

  // Same order as the collector: score, then username.
  rows.sort((a, b) => b.score - a.score || a.username.localeCompare(b.username))
  rows.forEach((row, index) => {
    row.rank = index + 1
  })

  return rows
}

/** A row of the `nuxter_profiles` table, without the sync timestamp. */
export interface NuxterProfileRow extends NuxterLinks {
  githubId: number
  username: string
  country: string | null
}

/** A known ISO 3166-1 alpha-2 country code, uppercased (`fr` → `FR`), or `null`. */
export function nuxterCountryCode(value: unknown): string | null {
  const code = typeof value === 'string' ? value.toUpperCase() : ''
  return Object.hasOwn(COUNTRIES, code) ? code : null
}

const MAX_SOCIAL_ACCOUNTS = 10
const MAX_TEXT_LENGTH = 200

function text(value: unknown, max = MAX_TEXT_LENGTH): string | null {
  return typeof value === 'string' && value.trim() ? value.trim().slice(0, max) : null
}

/** An `http(s)` URL, normalized, or `null`: profile links are user input, rendered as `href`. */
export function safeNuxterUrl(value: unknown): string | null {
  if (typeof value !== 'string' || value.length > 2048) return null
  try {
    const url = new URL(value.trim())
    return url.protocol === 'https:' || url.protocol === 'http:' ? url.href : null
  } catch {
    return null
  }
}

/**
 * Validate `profiles.json` and turn it into table rows.
 * Invalid entries and links are skipped, not fatal: one bad record must not block the import.
 */
export function parseNuxterProfilesFile(data: unknown): NuxterProfileRow[] {
  if (!Array.isArray(data)) {
    throw new TypeError('profiles.json must be an array')
  }

  const rows: NuxterProfileRow[] = []
  const seen = new Set<number>()
  for (const entry of data) {
    if (!entry || typeof entry !== 'object') continue
    const record = entry as Record<string, unknown>

    const githubId = Number(record.githubId)
    const username = text(record.username, 39)
    if (!Number.isSafeInteger(githubId) || githubId <= 0 || !username || seen.has(githubId)) continue

    const accounts = Array.isArray(record.social_accounts) ? record.social_accounts : []
    const socialAccounts: NuxterSocialAccount[] = []
    for (const account of accounts) {
      if (socialAccounts.length >= MAX_SOCIAL_ACCOUNTS) break
      if (!account || typeof account !== 'object') continue
      const { provider, url, display_name: displayName } = account as Record<string, unknown>
      const href = safeNuxterUrl(url)
      if (typeof provider !== 'string' || !/^[a-z]{1,32}$/.test(provider) || !href) continue
      socialAccounts.push({ provider, url: href, displayName: text(displayName) ?? href })
    }

    const name = text(record.name)
    const website = safeNuxterUrl(record.website)
    const country = nuxterCountryCode(record.country)
    if (!name && !website && !socialAccounts.length && !country) continue
    seen.add(githubId)
    rows.push({ githubId, username, name, website, socialAccounts, country })
  }
  return rows
}

export function rowToNuxter(row: Omit<NuxterRow, 'usernameLower' | 'firstContributionAt'> & { firstContributionAt: Date | string | null }): Nuxter {
  const first = row.firstContributionAt
  return {
    githubId: row.githubId,
    username: row.username,
    firstContributionAt: first instanceof Date ? first.toISOString() : first,
    rank: row.rank,
    score: row.score,
    issues: row.issues,
    helpfulIssues: row.helpfulIssues,
    comments: row.comments,
    helpfulComments: row.helpfulComments,
    reactions: row.reactions,
    mergedPullRequests: {
      feat: row.prFeat,
      fix: row.prFix,
      docs: row.prDocs,
      chore: row.prChore,
      all: row.prAll
    },
    core: {
      mergedPullRequests: {
        feat: row.corePrFeat,
        fix: row.corePrFix,
        docs: row.corePrDocs,
        chore: row.corePrChore,
        all: row.corePrAll
      },
      helpfulIssues: row.coreHelpfulIssues,
      helpfulComments: row.coreHelpfulComments
    },
    translations: {
      edited: row.translationsEdited,
      verified: row.translationsVerified,
      rejected: row.translationsRejected
    }
  }
}

export interface NuxterScoreRow {
  key: string
  label: string
  icon: string
  multiplier: number
  amount: number
  total: number
  /** Extra points for the core repositories, on top of the base rows. */
  bonus: boolean
  /** Segments of the docs translations, listed in their own group. */
  translation: boolean
}

/**
 * How the collector weighs each contribution type (see nuxt/nuxters `computeScore`).
 * Base rows first, then the translation rows, then the core bonus rows with contributions; each group
 * sorted by total. The totals add up to the score (the collector rounds the sum once).
 */
export function nuxterScoreBreakdown(nuxter: Nuxter): NuxterScoreRow[] {
  const prs = nuxter.mergedPullRequests
  const core = nuxter.core
  const translations = nuxter.translations ?? { edited: 0, verified: 0, rejected: 0 }
  const extra = NUXTERS_CORE_MULTIPLIER - 1
  type Row = Omit<NuxterScoreRow, 'total' | 'bonus' | 'translation'>
  const base: Row[] = [
    { key: 'feat', label: 'Feature PRs', icon: 'i-lucide-rocket', multiplier: 7, amount: prs.feat },
    { key: 'fix', label: 'Fix PRs', icon: 'i-lucide-wrench', multiplier: 5, amount: prs.fix },
    { key: 'docs', label: 'Docs PRs', icon: 'i-lucide-book-open', multiplier: 4, amount: prs.docs },
    { key: 'chore', label: 'Chore PRs', icon: 'i-lucide-brush-cleaning', multiplier: 3, amount: prs.chore },
    { key: 'helpfulIssues', label: 'Helpful issues', icon: 'i-lucide-lightbulb', multiplier: 3, amount: nuxter.helpfulIssues },
    { key: 'helpfulComments', label: 'Helpful comments', icon: 'i-lucide-message-circle-heart', multiplier: 2, amount: nuxter.helpfulComments },
    { key: 'issues', label: 'Issues', icon: 'i-lucide-circle-dot', multiplier: 1, amount: nuxter.issues },
    { key: 'comments', label: 'Comments', icon: 'i-lucide-message-circle', multiplier: 0.5, amount: nuxter.comments },
    { key: 'reactions', label: 'Reactions', icon: 'i-lucide-smile-plus', multiplier: 0.1, amount: nuxter.reactions }
  ]
  const translated: Row[] = [
    { key: 'translationsEdited', label: 'Translations edited', icon: 'i-lucide-pen-line', multiplier: 1, amount: translations.edited },
    { key: 'translationsVerified', label: 'Translations verified', icon: 'i-lucide-badge-check', multiplier: 0.5, amount: translations.verified },
    { key: 'translationsRejected', label: 'Translations rejected', icon: 'i-lucide-badge-x', multiplier: 0.5, amount: translations.rejected }
  ]
  const bonus: Row[] = [
    { key: 'coreFeat', label: 'Core feature PRs', icon: 'i-lucide-rocket', multiplier: 7 * extra, amount: core.mergedPullRequests.feat },
    { key: 'coreFix', label: 'Core fix PRs', icon: 'i-lucide-wrench', multiplier: 5 * extra, amount: core.mergedPullRequests.fix },
    { key: 'coreDocs', label: 'Core docs PRs', icon: 'i-lucide-book-open', multiplier: 4 * extra, amount: core.mergedPullRequests.docs },
    { key: 'coreChore', label: 'Core chore PRs', icon: 'i-lucide-brush-cleaning', multiplier: 3 * extra, amount: core.mergedPullRequests.chore },
    { key: 'coreHelpfulIssues', label: 'Core helpful issues', icon: 'i-lucide-lightbulb', multiplier: 3 * extra, amount: core.helpfulIssues },
    { key: 'coreHelpfulComments', label: 'Core helpful comments', icon: 'i-lucide-message-circle-heart', multiplier: 2 * extra, amount: core.helpfulComments }
  ]

  const withTotals = (rows: Row[], group: 'base' | 'translation' | 'bonus') => rows
    .map(row => ({ ...row, bonus: group === 'bonus', translation: group === 'translation', total: Math.round(row.amount * row.multiplier * 10) / 10 }))
    .sort((a, b) => b.total - a.total)

  return [
    ...withTotals(base, 'base'),
    ...withTotals(translated, 'translation'),
    ...withTotals(bonus, 'bonus').filter(row => row.amount > 0)
  ]
}

/** The Discord `nuxter` role needs one merged PR, helpful issue, helpful comment, or edited or verified translation. */
export function canUnlockNuxterRole(nuxter: Nuxter | null | undefined): boolean {
  if (!nuxter) return false
  return nuxter.mergedPullRequests.all + nuxter.helpfulIssues + nuxter.helpfulComments + nuxter.translations.edited + nuxter.translations.verified > 0
}

/**
 * The Discord role of the translators of a locale (`fr` → `translatorFr`, `pt-br` → `translatorPtBr`),
 * also its key in `runtimeConfig.nuxters.discord.roles`.
 */
export function nuxterTranslatorRole(locale: string): string {
  return `translator${locale.split('-').map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase()).join('')}`
}

export const NUXTERS_HACKATHONS: NuxtersHackathon[] = [
  {
    id: 'nuxtathon1',
    name: 'Nuxtathon #1',
    url: 'https://github.com/nuxt/nuxt/issues/35561',
    participants: [
      { githubId: '10813063', username: 'Flo0806' },
      { githubId: '89837724', username: 'Norbiros' },
      { githubId: '25607142', username: 'Mateleo' },
      { githubId: '37191683', username: 'OrbisK' },
      { githubId: '112722215', username: 'cernymatej' },
      { githubId: '245008322', username: 'stephanelgrg' },
      { githubId: '7257092', username: 'luc122c' },
      { githubId: '1107521', username: 'hacknug' },
      { githubId: '59548500', username: 'KealanAU' },
      { githubId: '5794325', username: 'yschroe' },
      { githubId: '21310742', username: 'MirkoJa' },
      { githubId: '24661232', username: 'Jorgagu' },
      { githubId: '48835293', username: 'DamianGlowala' },
      { githubId: '114825598', username: 'DarlanPrado' },
      { githubId: '2138260', username: 'Ibochkarev' },
      { githubId: '50132270', username: 'abaza738' },
      { githubId: '18102267', username: 'oritwoen' },
      { githubId: '22072217', username: 'onmax' },
      { githubId: '6538827', username: 'bdbch' },
      { githubId: '34019878', username: 'martinszeltins' },
      { githubId: '87276663', username: 'lorypelli' },
      { githubId: '22201189', username: 'lutejka' },
      { githubId: '58456495', username: 'chairulakmal' },
      { githubId: '35817344', username: 'chstappert' },
      { githubId: '7356077', username: 'connerblanton' },
      { githubId: '16652879', username: 'DevilTea' },
      { githubId: '85992002', username: 'KazariEX' },
      { githubId: '31246997', username: 'Niki2k1' },
      { githubId: '27751688', username: 'oneminch' },
      { githubId: '1766126', username: 'sumerokr' },
      { githubId: '43837308', username: 'wattanx' },
      { githubId: '35223685', username: 'yamachi4416' },
      { githubId: '28706372', username: 'danielroe' },
      { githubId: '5326365', username: 'harlan-zw' },
      { githubId: '640208', username: 'TheAlexLichter' },
      { githubId: '63512348', username: 'huang-julien' },
      { githubId: '1377702', username: 'serhalp' }
    ]
  },
  {
    id: 'nuxtathon2',
    name: 'Nuxtathon #2',
    url: 'https://github.com/nuxt/nuxt/issues/36438',
    participants: [
      { githubId: '246458', username: '0xgdr' },
      { githubId: '54577662', username: 'Aft1n' },
      { githubId: '42854121', username: 'aidanhibbard' },
      { githubId: '93380643', username: 'axh-xecoy' },
      { githubId: '739984', username: 'benjamincanac' },
      { githubId: '112722215', username: 'cernymatej' },
      { githubId: '80774326', username: 'cristianchiriac' },
      { githubId: '48835293', username: 'DamianGlowala' },
      { githubId: '83986256', username: 'devnoaman' },
      { githubId: '55502199', username: 'EvanSchleret' },
      { githubId: '10813063', username: 'Flo0806' },
      { githubId: '928780', username: 'genu' },
      { githubId: '39545125', username: 'giaBaoJS' },
      { githubId: '1107521', username: 'hacknug' },
      { githubId: '5265616', username: 'HtmAl' },
      { githubId: '71938701', username: 'HugoRCD' },
      { githubId: '15190246', username: 'hunterliu1003' },
      { githubId: '50110358', username: 'ianshk' },
      { githubId: '24661232', username: 'Jorgagu' },
      { githubId: '65376239', username: 'KayleeWilliams' },
      { githubId: '166915487', username: 'KazariAI' },
      { githubId: '85992002', username: 'KazariEX' },
      { githubId: '171180614', username: 'kazbekaskarov' },
      { githubId: '40021217', username: 'kingyue737' },
      { githubId: '37299685', username: 'lokyiec' },
      { githubId: '87276663', username: 'lorypelli' },
      { githubId: '7257092', username: 'luc122c' },
      { githubId: '7597086', username: 'mdanilowicz' },
      { githubId: '5103210', username: 'MickL' },
      { githubId: '39984251', username: 'Mini-ghost' },
      { githubId: '207940322', username: 'Neekoras' },
      { githubId: '3460368', username: 'nnx0r' },
      { githubId: '50957031', username: 'oleksiizlotnik' },
      { githubId: '22072217', username: 'onmax' },
      { githubId: '38668796', username: 'productdevbook' },
      { githubId: '25185835', username: 'Redemption198' },
      { githubId: '32015025', username: 'sadfun' },
      { githubId: '22557931', username: 'sean0407' },
      { githubId: '3428674', username: 'Waschnick' },
      { githubId: '43837308', username: 'wattanx' },
      { githubId: '35223685', username: 'yamachi4416' },
      { githubId: '2014360', username: 'yamcodes' },
      { githubId: '28706372', username: 'danielroe' },
      { githubId: '904724', username: 'atinux' },
      { githubId: '5326365', username: 'harlan-zw' },
      { githubId: '63512348', username: 'huang-julien' },
      { githubId: '640208', username: 'TheAlexLichter' }
    ]
  }
]

export function nuxterHackathons(githubId: string | number | undefined | null): NuxtersHackathon[] {
  if (!githubId) return []
  const id = String(githubId)
  return NUXTERS_HACKATHONS.filter(hackathon => hackathon.participants.some(p => p.githubId === id))
}
